/**
 * Sprint 58 · F2 — Store autoritativo de Plantillas de mesas.
 *
 * Consume EXCLUSIVAMENTE los endpoints F1 (`/api/plantillas`). El catálogo
 * de salones recibido del backend es la ÚNICA fuente autoritativa; el
 * frontend NO mantiene una segunda lista oficial. Cada operación persiste
 * de inmediato en backend (no hay estado sólo-local).
 */
import { api } from "@/services/api";

/** Normaliza un error de axios a `{ status, message }` con texto claro. */
const normalizarError = (err) => {
  const status = err?.response?.status || 0;
  const data = err?.response?.data;
  let detail = data?.detail ?? data;
  if (detail && typeof detail === "object") detail = detail.detail || detail.reason || detail.msg || null;
  const genericos = {
    400: "Datos inválidos. Revisa el formulario.",
    401: "Tu sesión expiró. Inicia sesión de nuevo.",
    403: "No tienes permiso para esta acción.",
    404: "La plantilla no existe.",
    409: "Conflicto: ya existe una plantilla con ese nombre en el mismo salón.",
  };
  return {
    status,
    message: (typeof detail === "string" && detail) || genericos[status] ||
      "No se pudo completar la operación. Intenta de nuevo.",
  };
};

const call = async (fn) => {
  try {
    const { data } = await fn();
    return data;
  } catch (err) {
    throw normalizarError(err);
  }
};

/** Catálogo oficial de salones (fuente autoritativa backend). */
export const getCatalogoSalones = async () => {
  const data = await call(() => api.get("/plantillas/salones-catalogo"));
  return Array.isArray(data?.salones) ? data.salones : [];
};

/** Listado con filtros opcionales `{ salon, activa }`. */
export const listarPlantillas = async ({ salon, activa } = {}) => {
  const params = {};
  if (salon) params.salon = salon;
  if (activa !== undefined && activa !== null) params.activa = activa;
  return call(() => api.get("/plantillas", { params }));
};

export const obtenerPlantilla = (id) => call(() => api.get(`/plantillas/${id}`));

export const crearPlantilla = (datos) => call(() => api.post("/plantillas", datos));

export const editarPlantilla = (id, datos) => call(() => api.put(`/plantillas/${id}`, datos));

export const activarPlantilla = (id) => call(() => api.post(`/plantillas/${id}/activar`));

export const desactivarPlantilla = (id) => call(() => api.post(`/plantillas/${id}/desactivar`));

/** Baja lógica (soft delete): la plantilla queda inactiva, no se borra. */
export const eliminarPlantilla = (id) => call(() => api.delete(`/plantillas/${id}`));

/** Aplica una plantilla a un evento (endpoint transaccional F1). */
export const aplicarPlantilla = (id, eventoId) =>
  call(() => api.post(`/plantillas/${id}/aplicar`, { eventoId }));

/**
 * Construye el payload de guardado desde el borrador del editor. SÓLO campos
 * estructurales; nunca precios ni datos comerciales. Estado forzado a
 * DISPONIBLE (el backend también lo exige).
 */
export const construirPayloadPlantilla = ({ nombre, salon, mesas }) => ({
  nombre: (nombre || "").trim(),
  salon,
  mesas: (Array.isArray(mesas) ? mesas : []).map((m) => ({
    id: m.id,
    numero: Number(m.numero),
    tipo: m.tipo || "",
    capacidad: Number(m.capacidad),
    estado: "DISPONIBLE",
    observaciones: m.observaciones || "",
    salon,
    x: m.x,
    y: m.y,
    rotacion: Number(m.rotacion) === 90 ? 90 : 0,
  })),
});
